<script setup>
import { ref } from "vue";
import ButtonCta from "@components/ButtonCta.vue";

const socials = [
    {
        name: "TIKTOK",
        icon: "https://wishlisted.s3.amazonaws.com/images/icons/icon-tiktok.svg",
        followers: "476k followers",
        url: "https://www.tiktok.com/@wishlistedgg",
        isNew: true,
    },
    {
        name: "TWITTER /X",
        icon: "https://wishlisted.s3.amazonaws.com/images/icons/icon-twitter.svg",
        followers: "57k followers",
        url: "https://x.com/wishlistedgg",
    },
    {
        name: "YOUTUBE",
        icon: "https://wishlisted.s3.amazonaws.com/images/icons/icon-youtube.svg",
        followers: "74k followers",
        url: "https://www.youtube.com/@wishlistedgg",
    },
    {
        name: "TWITCH",
        icon: "https://wishlisted.s3.amazonaws.com/images/icons/icon-twitch.svg",
        followers: "296k followers",
        url: "https://twitch.tv/wishlistedgg",
    },
    {
        name: "INSTAGRAM",
        icon: "https://wishlisted.s3.amazonaws.com/images/icons/icon-instagram.svg",
        followers: "15k followers",
        url: "https://www.instagram.com/wishlistedgg",
    },
];

const newsletterBgs = [
    {
        src: "https://wishlisted.s3.amazonaws.com/images/backgrounds/newsletter-bg.svg",
        inset: "inset-[-10px_-14px_-26px_-7px] md:inset-[-12px_-5px_-20px_-5px]",
    },
    {
        src: "https://wishlisted.s3.amazonaws.com/images/backgrounds/newsletter-bg-stroke.svg",
        inset: "inset-[-10px_-14px_-26px_-7px] top-[4px] md:top-0 md:inset-[-10px_-5px_-20px_-5px]",
    },
];

const success = ref(false);
const processing = ref(false);
const errors = ref({});
const newsletter = ref({
    email: null,
});

function submit(form) {
    form.preventDefault();
    processing.value = true;
    axios
        .post("/api/newsletter", newsletter.value)
        .then(() => {
            processing.value = false;
            success.value = true;
        })
        .catch((error) => {
            processing.value = false;
            success.value = false;
            if (error.response.status === 422) {
                errors.value = error.response.data.errors;
            }
        });
}
</script>

<template>
    <section
        id="follow-us"
        class="relative flex w-full flex-col items-start md:!pb-[200px]"
    >
        <!-- Decorative wave SVG 1 -->
        <img
            src="https://wishlisted.s3.amazonaws.com/images/ribbons/ribbon-follow-1.svg"
            alt=""
            class="pointer-events-none absolute bottom-[-335.48px] left-[-458px] h-[1220.483px] w-[1355.194px] -scale-y-100 rotate-[-154.75deg]"
        />

        <div class="container flex flex-col gap-[64px] md:gap-[120px]">
            <!-- BLOCK 1 — Social List -->
            <div
                class="relative z-10 flex w-full flex-col gap-[56px] md:grid lg:grid-cols-2 md:gap-x-[16px] md:gap-y-[17px]"
            >
                <!-- Label -->
                <div class="flex items-start gap-[8px]">
                    <img
                        src="https://wishlisted.s3.amazonaws.com/images/decorative/diamond-follow.svg"
                        alt=""
                        class="h-3 w-3"
                    />
                    <span
                        class="font-obviously font-bold text-[10px] uppercase leading-[1.4] tracking-[-0.12px] text-white md:text-[12px]"
                    >
                        Follow us
                    </span>
                </div>

                <!-- Social rows -->
                <div class="flex flex-col gap-[24px]">
                    <a
                        v-for="social in socials"
                        :key="social.name"
                        :href="social.url"
                        target="_blank"
                        rel="noopener noreferrer"
                        class="group flex flex-col items-start gap-[8px] transition-[padding] duration-300 ease-out-expo md:flex-row md:items-end md:gap-0 md:justify-between md:hover:px-[16px]"
                    >
                        <!-- Icon + name row -->
                        <div class="flex items-center gap-[12px]">
                            <img
                                :src="social.icon"
                                :alt="social.name"
                                class="social-icon h-8 w-8"
                            />
                            <span
                                class="font-obviously font-bold text-[24px] uppercase leading-none text-white transition-colors duration-300 ease-out-expo group-hover:text-primary md:text-[40px]"
                            >
                                {{ social.name }}
                            </span>
                        </div>

                        <!-- Follower count / New badge -->
                        <span
                            v-if="!social.isNew"
                            class="font-obviously-narrow text-[16px] uppercase leading-[1.4] tracking-[0.16px] text-primary"
                        >
                            {{ social.followers }}
                        </span>
                        <span
                            v-if="social.isNew"
                            class="inline-flex items-center justify-center rotate-[-4.76deg] md:rotate-[3.99deg] bg-primary px-[8px] py-[8px] font-obviously-narrow text-[16px] font-medium uppercase leading-[1.4] tracking-[0.16px] text-black"
                        >
                            new
                        </span>
                    </a>
                </div>
            </div>

            <!-- BLOCK 2 — Newsletter Card -->
            <div
                class="relative mb-10 z-10 flex h-[606px] w-full flex-col rounded-[16px] p-[32px] md:h-auto md:grid md:grid-cols-2 md:gap-x-[16px] md:gap-y-[17px] md:p-[80px]"
            >
                <!-- Background layers -->
                <div
                    v-for="(bg, i) in newsletterBgs"
                    :key="i"
                    class="pointer-events-none absolute flex items-center justify-center"
                    :class="bg.inset"
                >
                    <div
                        class="h-full w-full rotate-[-3.72deg] skew-x-[-2.35deg] md:rotate-[-1.43deg] md:skew-x-0"
                    >
                        <img
                            :src="bg.src"
                            alt=""
                            class="block h-full w-full max-w-none"
                        />
                    </div>
                </div>

                <!-- Large W watermark -->
                <img
                    src="https://wishlisted.s3.amazonaws.com/images/logos/logo-newsletter.avif"
                    alt=""
                    class="pointer-events-none absolute left-[18%] top-[-11%] h-[47%] w-auto aspect-9/10 rotate-[-16.15deg] md:left-[3%] md:top-[-13%] md:h-[65%] md:w-auto md:aspect-9/10 md:-rotate-[7.68deg] lg:h-[98%]"
                />

                <!-- Card content -->
                <div
                    class="relative z-10 mt-auto flex flex-col gap-[24px] md:col-start-2 md:mt-0"
                >
                    <span
                        class="font-obviously font-bold text-[12px] uppercase leading-none tracking-[-0.12px] text-black"
                    >
                        Newsletter
                    </span>

                    <h2
                        class="font-obviously font-bold text-[40px] leading-[0.9] text-black"
                    >
                        Don't Miss a Beat
                    </h2>

                    <p
                        class="font-obviously-narrow text-[16px] leading-[1.4] tracking-[0.16px] text-black"
                    >
                        Stay updated on showcase news and future events with our
                        newsletter.
                    </p>

                    <!-- Form -->
                    <form
                        class="flex w-full flex-col gap-[12px] md:flex-row md:items-end"
                        @submit="submit"
                    >
                        <input
                            type="email"
                            maxlength="128"
                            placeholder="your@email.com"
                            class="h-[48px] w-full rounded-[8px] border-2 border-black/10 bg-transparent px-[16px] font-obviously-narrow text-[16px] tracking-[0.16px] text-black placeholder:opacity-40 focus:outline-none md:flex-1"
                            v-model="newsletter.email"
                        />

                        <ButtonCta
                            label="Subscribe"
                            variant="dark"
                            borderColor="primary"
                            type="submit"
                            class="w-full md:w-auto"
                        />
                    </form>
                    <div
                        v-if="success"
                        class="bg-black rounded-md text-primary font-medium w-fit p-1 px-2 -mt-4"
                    >
                        Thank you for subscribing!
                    </div>
                </div>
            </div>
        </div>
    </section>
</template>

<style scoped>
.social-icon {
    transition: filter 0.3s cubic-bezier(0.16, 1, 0.3, 1);
}

.group:hover .social-icon {
    filter: brightness(0) saturate(100%) invert(78%) sepia(60%) saturate(1000%)
        hue-rotate(3deg) brightness(103%) contrast(96%);
}
</style>
